// src/ForgotPassword.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import api from "./Api";

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(email || "").trim());

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    const trimmedEmail = email.trim();

    // Validación
    if (!trimmedEmail) {
      setError("Debes ingresar tu email");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError("El email no es válido");
      return;
    }

    setLoading(true);

    try {
      const res = await api.put("", {
        action: "forgot_password",
        email: trimmedEmail,
      });

      if (res.data?.success) {
        setSuccess(true);
        setEmail("");
      } else {
        setError(res.data?.error || "No se pudo enviar el email");
      }
    } catch (err) {
      setError("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#0f172a",
        margin: 0,
        padding: 0,
      }}
    >
      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        <Paper elevation={0} sx={card}>
          {/* Botón volver */}
          <Box sx={{ mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/")}
              sx={{ color: "#89b4ff", textTransform: "none" }}
            >
              Volver al login
            </Button>
          </Box>

          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Avatar sx={avatar}>
              <LockResetIcon />
            </Avatar>
            <Typography variant="h5" sx={title}>
              ¿Olvidaste tu contraseña?
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.75)", mt: 1, fontSize: 14 }}>
              Ingresa tu email y te enviaremos una contraseña temporal
            </Typography>
          </Box>

          {/* Formulario */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              variant="standard"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputLabelProps={{ sx: label }}
              InputProps={{ sx: underlineInput }}
              sx={{ mb: 2 }}
              required
              disabled={success}
            />

            {error && <Typography sx={errorBox}>{error}</Typography>}

            {success && (
              <Box sx={successBox}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  ✅ Email enviado correctamente
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  Revisa tu bandeja de entrada. Te hemos enviado una contraseña temporal.
                </Typography>
                <Button
                  onClick={() => navigate("/")}
                  sx={{ ...ctaBtn, mt: 2 }}
                >
                  Ir al login
                </Button>
              </Box>
            )}

            {!success && (
              <Button type="submit" fullWidth disabled={loading} sx={ctaBtn}>
                {loading ? "Enviando..." : "Enviar contraseña temporal"}
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

/* ---------- Estilos ---------- */
const card = {
  p: 4,
  borderRadius: 4,
  bgcolor: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(12px)",
  boxShadow: "0 10px 30px rgba(0,0,0,.35), inset 0 0 0 1px rgba(255,255,255,.04)",
};

const avatar = {
  m: "10px auto 0",
  width: 58,
  height: 58,
  background: "linear-gradient(135deg, #6a7dff 0%, #9b59ff 50%, #21e6b6 100%)",
  boxShadow: "0 8px 22px rgba(105,90,255,.45)",
};

const title = {
  mt: 1,
  fontWeight: 800,
  letterSpacing: 0.6,
  background: "linear-gradient(90deg,#e6e8ff 0%, #d8c5ff 50%, #a5ffe7 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

const label = { color: "rgba(255,255,255,.7) !important" };

const underlineInput = {
  color: "rgba(255,255,255,.95)",
  "&:before": { borderBottom: "1px solid rgba(255,255,255,.25)" },
  "&:hover:not(.Mui-disabled):before": {
    borderBottom: "1px solid rgba(255,255,255,.45)",
  },
  "&:after": {
    borderBottom: "2px solid #79e9d0",
    boxShadow: "0 6px 18px rgba(121,233,208,.35)",
  },
};

const errorBox = {
  mt: 1.5,
  p: 1,
  borderRadius: 2,
  fontSize: 13,
  color: "#ffb3b3",
  bgcolor: "rgba(255,0,0,.12)",
  border: "1px solid rgba(255,0,0,.25)",
};

const successBox = {
  mt: 1.5,
  p: 2,
  borderRadius: 2,
  fontSize: 13,
  color: "#b3ffb3",
  bgcolor: "rgba(0,255,0,.12)",
  border: "1px solid rgba(0,255,0,.25)",
};

const ctaBtn = {
  mt: 3,
  py: 1.4,
  borderRadius: 999,
  fontWeight: 800,
  letterSpacing: 0.4,
  color: "white",
  background: "linear-gradient(90deg, #6a7dff 0%, #9b59ff 50%, #21e6b6 100%)",
  boxShadow:
    "0 12px 26px rgba(105,90,255,.45), 0 8px 18px rgba(33,230,182,.25)",
  textTransform: "none",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow:
      "0 14px 30px rgba(105,90,255,.55), 0 10px 22px rgba(33,230,182,.35)",
  },
  transition: "all .2s ease",
};
